import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { usePrivacyConsent } from "@/hooks/usePrivacyConsent";
import {
  acknowledgeAnonymousPrivacy,
  hasUserAcceptedTerms,
  readPrivacyBannerStorageState,
  shouldShowPrivacyBanner,
  snoozePrivacyBanner,
} from "@/lib/privacyConsent";

function getPrimaryButtonLabel(isSignedIn: boolean, accepting: boolean): string {
  if (!isSignedIn) {
    return "Got it";
  }
  if (accepting) {
    return "Saving…";
  }
  return "Accept Policy";
}

/**
 * Privacy policy consent banner for public pages (RootLayout).
 * Logged-in users: Accept records consent via API; "Not now" snoozes the banner.
 * Anonymous users: Informational only; "Got it" stores local acknowledgment.
 */
export function PrivacyPolicyConsent() {
  const { user } = useAuth();
  const { consentData, loading, giveConsent } = usePrivacyConsent();
  const [bannerState, setBannerState] = useState(readPrivacyBannerStorageState);
  const [accepting, setAccepting] = useState(false);

  const visible = shouldShowPrivacyBanner({
    isAuthenticated: Boolean(user),
    hasConsented: consentData.hasConsented,
    consentLoading: Boolean(user) && loading,
    isSnoozed: bannerState.isSnoozed,
    hasAnonymousAcknowledged: bannerState.hasAnonymousAcknowledged,
    hasTermsAccepted: hasUserAcceptedTerms(user),
  });

  if (!visible) {
    return null;
  }

  const handleNotNow = () => {
    snoozePrivacyBanner();
    setBannerState(readPrivacyBannerStorageState());
  };

  /**
   * Handle Primary Action.
   */
  const handlePrimaryAction = async () => {
    if (!user) {
      acknowledgeAnonymousPrivacy();
      setBannerState(readPrivacyBannerStorageState());
      return;
    }

    setAccepting(true);
    try {
      const ok = await giveConsent();
      if (ok) {
        setBannerState(readPrivacyBannerStorageState());
      }
    } finally {
      setAccepting(false);
    }
  };

  return (
    <Card
      className="fixed bottom-4 right-4 w-80 z-50 border-blue-200 shadow-lg animate-in fade-in slide-in-from-bottom-10 duration-300"
      role="dialog"
      aria-labelledby="privacy-consent-title"
      aria-describedby="privacy-consent-description"
    >
      <CardContent className="p-4">
        <h3 id="privacy-consent-title" className="text-lg font-semibold text-blue-800 mb-2 pr-1">
          Privacy Policy
        </h3>

        <p id="privacy-consent-description" className="text-sm text-gray-700 mb-4">
          {user ? (
            <>
              Please review and accept our{" "}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </Link>{" "}
              so we can record your consent for data processing.
            </>
          ) : (
            <>
              We use cookies and process data as described in our{" "}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </Link>
              . You can review it anytime; sign in later to record formal consent for your account.
            </>
          )}
        </p>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-gray-600"
            onClick={handleNotNow}
          >
            Not now
          </Button>
          <Button
            type="button"
            onClick={() => void handlePrimaryAction()}
            disabled={accepting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {getPrimaryButtonLabel(Boolean(user), accepting)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
